import * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import {useEffect, useState} from "react";
import {Button, Checkbox, FormControlLabel, FormGroup, TextField} from "@mui/material";
import {ContactRole, OperationContactRec, OperationContactSave} from "./OperationContactApi.ts";
import clsx from "clsx";
import css from "./OperationalContactEdit.module.css";
// @ts-ignore
import {Theme} from "../../Components/styles/styles";

export interface OperationalContact {
    contacts: OperationContactRec[];
    contactRole: ContactRole;
    onSave: (contacts: OperationContactRec[]) => void;
    onDelete: (companyIds: number[]) => void;
    onUpdate: (contacts: OperationContactRec[], companyIds: number[]) => void;
    onCancel: () => void;
    saveStatus: OperationContactSave[];
    onChanged: (hasChanges: boolean) => void;
}

export const OperationalContactEdit: React.FC<OperationalContact> = (
    props: OperationalContact
) => {

    const contactBoxRefs = React.useRef<Map<number | string, HTMLDivElement>>(new Map());
    const [contacts, setContacts] = useState(
        (props.contacts ?? []).map(contact => ({...contact}))
    );
    const [contactsLength, setContactsLength] = useState<Number>(0);
    const [changed, setChanged] = useState(false);
    const [toRemove, setToRemove] = useState<number[]>([]);


    const handleChange = (index: number, field: string, value: any) => {
        const updated = [...contacts];
        updated[index] = {...updated[index], [field]: value};
        setContacts(updated);
        setChanged(true);
    };


    const handleRoleToggle = (contactIndex: number, roleKey: string) => {
        const updated = [...contacts];
        const currentRoles = updated[contactIndex].role || [];

        // @ts-ignore
        const newRoles = currentRoles.includes(roleKey)
            ? currentRoles.filter(r => r !== roleKey)
            : [...currentRoles, roleKey];

        updated[contactIndex] = {...updated[contactIndex], role: newRoles};
        setContacts(updated);
        setChanged(true);
    };


    const handleRemove = (companyId: number) => {
        handleExternalEvent(companyId, "delete");
        setToRemove([...toRemove, companyId]);
    };


    const handleAdd = () => {
        const newContact: OperationContactRec = {
            id: -1 * Date.now().valueOf(),
            name: '',
            description: '',
            phone: '',
            email: '',
            role: []
        };
        setContacts([...contacts, newContact]);
        setChanged(true);
    };


    const onSave = (contacts: OperationContactRec[]): void => {
        if (changed && toRemove.length) {
            props.onUpdate(contacts, toRemove);
        } else if (changed) {
            props.onSave(contacts);
        } else if (toRemove.length) {
            props.onDelete(toRemove);
            setToRemove([]);
        }
        setChanged(false)
    }

    const handleExternalEvent = (targetId: number, status: 'error' | 'success' | 'clear' | 'delete') => {
        const boxElement = contactBoxRefs.current.get(targetId);

        if (boxElement) {
            if (status === 'error') {
                boxElement.classList.add(css.error);
            } else if (status === 'success') {
                boxElement.style.border = "2px solid green";
            } else if (status === 'delete') {
                boxElement.classList.add(css.delete);
            } else {
                boxElement.style.border = "";
                boxElement.style.backgroundColor = "";
            }
        }
    };


    useEffect(() => {
        props.onChanged(changed);
    }, [changed]);


    useEffect(() => {
        setContacts([...(props.contacts ?? [])]);
    }, [props.contacts]);


    useEffect(() => {
        if (contacts.length !== contactsLength) {
            setContactsLength(contacts.length);
        }
    }, [contacts.length]);


    useEffect(() => {
        if (props.saveStatus && props.saveStatus.length > 0) {
            setContacts(saveStatus => {
                const updated = saveStatus.map(contact => {
                    const savedStatus = props.saveStatus.find(status => status.tmpId === contact.id);
                    if (savedStatus && savedStatus.id) {
                        const c = {...contact, id: savedStatus.id};
                        return c;
                    }
                    return contact;

                }).filter(contact => {
                    const savedStatus = props.saveStatus.find(status => status.tmpId === contact.id);
                    return !(savedStatus?.type === 'DELETE' && savedStatus.status === 'SUCCESS');
                });
                return updated;
            });
        }
    }, [props.saveStatus]);


    const oneContact = (index: number, contact: OperationContactRec) => {
        return (
            <Box
                key={contact.id} className={clsx(css.item)}
                ref={(el: any) => {
                    if (el) {
                        contactBoxRefs.current.set(contact.id, el);
                    } else {
                        contactBoxRefs.current.delete(contact.id);
                    }
                }}
            >

                <Box className={clsx(css.contact)}>
                    <TextField
                        className={clsx(css.textField)}
                        label="Nazwa"
                        fullWidth
                        size="small"
                        value={contact.name || ''}
                        onChange={(e) => handleChange(index, 'name', e.target.value)}
                        margin="normal"
                    />
                    <TextField
                        className={clsx(css.textField)}
                        label="Opis"
                        fullWidth
                        size="small"
                        multiline
                        value={contact.description || ''}
                        onChange={(e) => handleChange(index, 'description', e.target.value)}
                        margin="normal"
                    />
                    <Box sx={{display: 'flex', gap: 2}}>
                        <TextField
                            className={clsx(css.textField)}
                            label="Telefon"
                            fullWidth
                            size="small"
                            value={contact.phone || ''}
                            onChange={(e) => handleChange(index, 'phone', e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            className={clsx(css.textField)}
                            label="Mail"
                            fullWidth
                            size="small"
                            value={contact.email || ''}
                            onChange={(e) => handleChange(index, 'email', e.target.value)}
                            margin="normal"
                        />
                    </Box>

                    <Box className={clsx(css.separator)} sx={{my: 2, fontWeight: 'bold'}}>
                        Rola w przedsiębiorstwie
                    </Box>

                    <FormGroup row>
                        {Object.entries(props.contactRole ?? {}).map(([key, label]) => {
                            return (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Checkbox
                                            checked={(contact.role || []).includes(key)}
                                            onChange={() => handleRoleToggle(index, key)}
                                        />
                                    }
                                    label={String(label)}
                                    sx={{minWidth: '200px'}}
                                />
                            );
                        })}
                    </FormGroup>

                    <Divider sx={{my: 2}}/>

                    <Button onClick={() => handleRemove(contact.id)}>
                        Usuń
                    </Button>
                </Box>
            </Box>

        )
    }

    return (
        <Box className={clsx(css.operationContactEdit)}>

            <Box className={clsx(css.data)}>
                {contacts.map((contact, index) => {
                    return (
                        oneContact(index, contact)
                    );
                })}
                <Box>
                    <Button onClick={handleAdd}>
                        Dodaj kontakt
                    </Button>
                </Box>

            </Box>

            <Divider sx={{my: 2}}/>

            <Box id="ButtonsCancelAccept"
                 sx={{
                     display: 'flex',
                     gap: 2,
                     justifyContent: 'flex-end'
            }}
            >

                <Button
                    variant="outlined"
                    onClick={props.onCancel}
                    sx={{
                        ...Theme.outlinedOrangeButton,
                        [Theme.breakpoints.down("laptop")]: {
                            fontSize: 12,
                            width: 90,
                        },
                    }}
                >
                    {(!changed && toRemove.length === 0) ? 'Zamknij' : 'Anuluj zmiany'}

                </Button>
                <Button
                    disabled={!changed && toRemove.length === 0}
                    variant="outlined"
                    onClick={() => onSave(contacts)}
                    sx={{
                        ...Theme.OrangeButton,
                        fontSize: 14,
                        minWidth: 111,

                        [Theme.breakpoints.down("laptop")]: {
                            fontSize: 12,
                        },
                    }}

                >
                    Zapisz zmiany
                </Button>

            </Box>
        </Box>
    );
};